import { zodResolver } from '@hookform/resolvers/zod';
import {
  userTypesControllerCreate,
  userTypesControllerList,
  userTypesControllerSoftDelete,
  userTypesControllerUpdate,
} from '@kalba/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { Pencil, Plus, Search, Trash2, UserCog, X } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { AppLayout } from '../../components/layout/app-layout';
import { PageHeader } from '../../components/layout/page-header';
import {
  CardDataTable,
  type CardDataTableColumn,
  type CardDataTableRowDef,
} from '../../components/shared/card-data-table';
import { StatCard } from '../../components/shared/stat-card';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { usePermissions } from '../../hooks/use-permissions';
import { cn } from '../../lib/utils';
import { useAuthStore } from '../../stores/auth.store';
import { useUserTypesStore } from '../../stores/config/user-types.store';

const userTypeSchema = z.object({ name: z.string().min(1, 'Name is required') });

// ── Types ─────────────────────────────────────────────────────────────────────

interface UserType {
  id: string;
  name: string;
  code: string;
  displayOrder: number;
  status: string;
  deletedAt?: string | null;
}

// ── Route ─────────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/config/user-types')({
  beforeLoad: () => {
    const { user, permissions } = useAuthStore.getState();
    if (!user) throw redirect({ to: '/login' });
    if (!permissions.includes('config:view')) throw redirect({ to: '/dashboard' });
  },
  component: UserTypesPage,
});

// ── Helpers ───────────────────────────────────────────────────────────────────

function UserTypeAvatar({ name }: { name: string }) {
  const colors = [
    'from-blue-500 to-indigo-600',
    'from-emerald-500 to-teal-600',
    'from-amber-500 to-orange-600',
    'from-violet-500 to-purple-600',
  ];
  const idx = (name.charCodeAt(0) + name.length) % colors.length;
  return (
    <div
      className={cn(
        'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br text-white shadow-sm',
        colors[idx],
      )}
    >
      <UserCog size={18} />
    </div>
  );
}

function generatePreviewCode(name: string): string {
  return (
    name
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '_')
      .replace(/^_|_$/g, '') || '…'
  );
}

// ── List columns ──────────────────────────────────────────────────────────────

const COLUMNS: CardDataTableColumn[] = [
  { id: 'userType', header: 'User Type' },
  { id: 'code', header: 'Code' },
  { id: 'status', header: 'Status', align: 'center' },
];

const GRID = 'minmax(0,1fr) 200px 110px';

// ── Page ──────────────────────────────────────────────────────────────────────

function UserTypesPage() {
  const { can } = usePermissions();
  const qc = useQueryClient();
  const canEdit = can('user-type:edit');
  const canDelete = can('user-type:delete');

  const {
    search,
    debounced,
    addOpen,
    editTarget: _editTarget,
    deleteConfirm: _deleteConfirm,
    deleteError,
    setSearch,
    setDebounced,
    setAddOpen,
    setEditTarget,
    setDeleteConfirm,
    setDeleteError,
  } = useUserTypesStore();
  const editTarget = _editTarget as UserType | null;
  const deleteConfirm = _deleteConfirm as UserType | null;
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const createForm = useForm<{ name: string }>({
    resolver: zodResolver(userTypeSchema),
    defaultValues: { name: '' },
  });
  const editForm = useForm<{ name: string }>({
    resolver: zodResolver(userTypeSchema),
    defaultValues: { name: '' },
  });

  useEffect(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setDebounced(search), 250);
    return () => clearTimeout(timerRef.current);
  }, [search]);

  useEffect(() => {
    if (editTarget) editForm.reset({ name: editTarget.name });
  }, [editTarget?.id]);

  const { data: userTypes = [], isLoading } = useQuery<UserType[]>({
    queryKey: ['userTypes'],
    queryFn: () => userTypesControllerList() as Promise<UserType[]>,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['userTypes'] });

  const createMutation = useMutation({
    mutationFn: (data: { name: string }) =>
      userTypesControllerCreate({ requestBody: { name: data.name.trim() } }),
    onSuccess: () => {
      invalidate();
      setAddOpen(false);
      createForm.reset();
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: { name: string }) =>
      userTypesControllerUpdate({
        id: editTarget!.id,
        requestBody: { name: data.name.trim() },
      }),
    onSuccess: () => {
      invalidate();
      setEditTarget(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => userTypesControllerSoftDelete({ id }),
    onSuccess: () => {
      invalidate();
      setDeleteConfirm(null);
      setDeleteError(null);
    },
    onError: async (err: unknown) => {
      try {
        const body = (await (err as { response?: Response }).response?.json()) as
          | { message?: string }
          | undefined;
        setDeleteError(body?.message ?? 'Delete failed');
      } catch {
        setDeleteError('Delete failed');
      }
    },
  });

  const totalTypes = userTypes.length;
  const activeCount = userTypes.filter((t) => t.status === 'ACTIVE').length;
  const inactiveCount = userTypes.filter((t) => t.status !== 'ACTIVE').length;

  const filtered = userTypes.filter((t) => {
    if (!debounced) return true;
    const q = debounced.toLowerCase();
    return t.name.toLowerCase().includes(q) || t.code.toLowerCase().includes(q);
  });

  return (
    <AppLayout>
      <PageHeader
        title="User Types"
        description="Configurable list of user types (e.g. Beeah Consultant, SM Consultant) selectable on user profiles."
        actions={
          canEdit ? (
            <Button variant="success" onClick={() => setAddOpen(true)}>
              <Plus size={16} />
              New User Type
            </Button>
          ) : null
        }
      />

      {/* Stat cards */}
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-3">
        <StatCard
          icon={<UserCog size={18} />}
          iconBg="bg-blue-50"
          iconColor="text-blue-500"
          delta={`${activeCount} active · ${inactiveCount} inactive`}
          value={totalTypes}
          label="Total Types"
          loading={isLoading}
        />
        <StatCard
          icon={<UserCog size={18} />}
          iconBg="bg-emerald-50"
          iconColor="text-emerald-500"
          delta="Currently enabled"
          value={activeCount}
          label="Active"
          loading={isLoading}
        />
        <StatCard
          icon={<UserCog size={18} />}
          iconBg="bg-gray-100"
          iconColor="text-gray-400"
          delta="Soft deleted"
          value={inactiveCount}
          label="Inactive"
          loading={isLoading}
        />
      </div>

      {/* Toolbar */}
      <div className="mb-4 flex items-center gap-3 rounded-2xl border border-gray-200 bg-white p-3 shadow-sm">
        <div className="relative min-w-60 flex-1">
          <Search
            size={14}
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or code…"
            className="h-9 w-full rounded-xl border border-gray-200 bg-gray-50 pl-10 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-brand-100 focus:border-brand-400 transition-all placeholder:text-gray-400"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X size={13} />
            </button>
          )}
        </div>
        <span className="ml-auto text-xs text-gray-400">
          {filtered.length} of {totalTypes} types
        </span>
      </div>

      {/* List */}
      <CardDataTable
        gridTemplateColumns={GRID}
        columns={COLUMNS}
        isLoading={isLoading}
        emptyContent={
          <div className="rounded-2xl border border-gray-200 bg-white p-16 text-center shadow-sm">
            <UserCog size={40} className="mx-auto mb-4 text-gray-200" />
            <p className="text-sm font-semibold text-gray-500">No user types found</p>
            <p className="mt-1 text-xs text-gray-400">
              {search ? 'Try adjusting your search' : 'Add your first user type to get started'}
            </p>
          </div>
        }
        rows={filtered.map(
          (ut): CardDataTableRowDef => ({
            id: ut.id,
            avatar: <UserTypeAvatar name={ut.name} />,
            cells: {
              userType: (
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-gray-900 leading-snug">
                    {ut.name}
                  </p>
                </div>
              ),
              code: (
                <span className="rounded-md border border-gray-200 bg-gray-50 px-2 py-0.5 font-mono text-xs font-semibold text-gray-500">
                  {ut.code}
                </span>
              ),
              status: (
                <span
                  className={cn(
                    'inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold',
                    ut.status === 'ACTIVE'
                      ? 'bg-success-50 text-success-700 border border-success-200'
                      : 'bg-gray-100 text-gray-500 border border-gray-200',
                  )}
                >
                  {ut.status === 'ACTIVE' ? 'Active' : 'Inactive'}
                </span>
              ),
            },
            ...(canEdit || canDelete
              ? {
                  actions: [
                    ...(canEdit
                      ? [
                          {
                            icon: <Pencil size={13} />,
                            label: 'Edit',
                            onClick: (e: React.MouseEvent) => {
                              e.stopPropagation();
                              setEditTarget(ut);
                            },
                          },
                        ]
                      : []),
                    ...(canDelete
                      ? [
                          {
                            icon: <Trash2 size={13} className="text-error-500" />,
                            label: 'Delete',
                            onClick: (e: React.MouseEvent) => {
                              e.stopPropagation();
                              setDeleteConfirm(ut);
                              setDeleteError(null);
                            },
                          },
                        ]
                      : []),
                  ],
                }
              : {}),
          }),
        )}
      />

      {/* Create dialog */}
      <Dialog
        open={addOpen}
        onOpenChange={(v) => {
          if (!v) {
            setAddOpen(false);
            createForm.reset();
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>New User Type</DialogTitle>
          </DialogHeader>
          <form onSubmit={createForm.handleSubmit((d) => createMutation.mutate(d))}>
            <div className="space-y-4 py-2">
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-600">
                  Name <span className="text-error-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. Beeah Consultant"
                  className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-100 focus:border-brand-400 transition-all"
                  {...createForm.register('name')}
                />
                {createForm.formState.errors.name && (
                  <p className="mt-1 text-xs text-error-500">
                    {createForm.formState.errors.name.message}
                  </p>
                )}
              </div>
              {createForm.watch('name')?.trim() && (
                <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5">
                  <span className="text-xs text-gray-500">Code preview:</span>
                  <span className="font-mono text-xs font-bold text-gray-700">
                    {generatePreviewCode(createForm.watch('name'))}
                  </span>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={() => {
                  setAddOpen(false);
                  createForm.reset();
                }}
                disabled={createMutation.isPending}
              >
                Cancel
              </Button>
              <Button variant="success" size="sm" type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Creating…' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog
        open={!!editTarget}
        onOpenChange={(v) => {
          if (!v) setEditTarget(null);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User Type</DialogTitle>
          </DialogHeader>
          <form onSubmit={editForm.handleSubmit((d) => updateMutation.mutate(d))}>
            <div className="space-y-4 py-2">
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-600">
                  Name <span className="text-error-500">*</span>
                </label>
                <input
                  type="text"
                  className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-100 focus:border-brand-400 transition-all"
                  {...editForm.register('name')}
                />
                {editForm.formState.errors.name && (
                  <p className="mt-1 text-xs text-error-500">
                    {editForm.formState.errors.name.message}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5">
                <span className="text-xs text-gray-500">Code (immutable):</span>
                <span className="font-mono text-xs font-bold text-gray-700">
                  {editTarget?.code}
                </span>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={() => setEditTarget(null)}
                disabled={updateMutation.isPending}
              >
                Cancel
              </Button>
              <Button variant="success" size="sm" type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? 'Saving…' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog
        open={!!deleteConfirm}
        onOpenChange={(v) => {
          if (!v) {
            setDeleteConfirm(null);
            setDeleteError(null);
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete User Type</DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <p className="text-sm text-gray-700">
              Delete <span className="font-semibold">{deleteConfirm?.name}</span>?
            </p>
            <p className="text-xs text-gray-500">
              Users currently assigned this type will keep their existing assignment; the type will
              no longer be selectable for new assignments.
            </p>
            {deleteError && (
              <p className="rounded-xl border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-600">
                {deleteError}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setDeleteConfirm(null);
                setDeleteError(null);
              }}
              disabled={deleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => deleteMutation.mutate(deleteConfirm!.id)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
